import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from '@/components/shared/PageHeader';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { Separator } from '@/components/ui/separator';
import { SEO } from '@/components/SEO';

export default function DirectorPage() {
  const pacoSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://baklight.com/director#paco-herrera",
    "name": "Paco Herrera",
    "jobTitle": ["Director", "Cinematographer", "Producer"],
    "description": "Mexican director and cinematographer whose work explores identity, place and human connection through cinematic storytelling. Over 20 years of experience in documentary, narrative, and commercial filmmaking.",
    "url": "https://baklight.com/director",
    "image": "https://filmfreeway-production-storage-01-connector.filmfreeway.com/press_kits/headshots/003/639/002/original/2790d4ab42-headshot.jpg?1758508093",
    "nationality": {
      "@type": "Country",
      "name": "Mexico"
    },
    "worksFor": {
      "@type": "Organization",
      "@id": "https://baklight.com/#organization",
      "name": "Baklight"
    },
    "foundingOrganization": {
      "@type": "Organization",
      "@id": "https://baklight.com/#organization",
      "name": "Baklight"
    },
    "award": [
      "Best Short Documentary — Silver Screen International Film Festival (Prague)",
      "Best Story — Toronto/LA Documentary Festival",
      "Excellence Award — Docs Without Borders",
      "12+ Telly Awards",
      "Kodak Film Student Scholarship (1999)",
      "IMCINE Production Grants"
    ],
    "knowsAbout": [
      "Documentary Filmmaking",
      "Cinematography",
      "Film Direction",
      "Commercial Production",
      "Narrative Filmmaking",
      "Color Grading",
      "Visual Storytelling"
    ],
    "alumniOf": {
      "@type": "EducationalOrganization",
      "name": "Film School"
    },
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "name": "Kodak Film Student Scholarship",
        "dateCreated": "1999"
      }
    ],
    "sameAs": [
      "https://www.imdb.com/name/nm6885706/",
      "https://filmfreeway.com/PacoHerrera"
    ]
  };

  return (
    <div>
      <SEO 
        title="Paco Herrera — Director & Cinematographer | Baklight Films"
        description="Mexican director and cinematographer. 20+ years in documentary, narrative, and commercial filmmaking. Award-winning work at international festivals."
        path="/director"
      />
      
      {/* Person Schema */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(pacoSchema)}
        </script>
      </Helmet>
      <PageHeader
        meta="Director & Cinematographer"
        title="Paco Herrera"
      />

      {/* Portrait + Bio */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            {/* Image floats left, text wraps around */}
            <div className="space-y-6">
              {/* Floating Image */}
              <div className="float-left w-[280px] sm:w-[320px] md:w-[380px] mr-8 mb-6">
                <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.6)]">
                  <img
                    src="https://filmfreeway-production-storage-01-connector.filmfreeway.com/press_kits/headshots/003/639/002/original/2790d4ab42-headshot.jpg?1758508093"
                    alt="Paco Herrera"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Quote */}
              <p className="font-display text-xl md:text-2xl text-[hsl(var(--bk-fg))] leading-snug mb-6">
                "Every image carries the weight of a decision. The camera doesn't observe — it interprets."
              </p>
              
              <Separator className="bg-[hsl(var(--bk-line))] mb-6" />
              
              {/* Bio Text - wraps around image */}
              <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] mb-4">
                Paco Herrera is a Mexican director and cinematographer whose work explores identity, place and human connection through cinematic language.
              </p>
              <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] mb-4">
                With over two decades of experience across documentary, narrative and commercial filmmaking, he approaches each project as both story and visual construction — shaping tone, rhythm and image from concept through final grade.
              </p>
              <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] mb-4">
                His work has been recognized at international festivals including Silver Screen (Prague), London Vision, Berlin Kiez, Berlin Lift-Off, Toronto/LA Documentary Festival, Austin Lift-Off, and Hollywood ShortsFest. Recipient of the Kodak Film Student Scholarship and multiple IMCINE production grants.
              </p>
              <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] mb-6">
                As cinematographer, Paco has contributed to animated features including <em>El Secreto del Medallón de Jade</em>, <em>Monster Island</em>, and <em>Cranston Academy</em> (Netflix). His commercial work spans The Patrón Spirits Company, National Geographic, and HBO.
              </p>
              
              {/* Clear float before awards section */}
              <div className="clear-both pt-4">
                <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-4">Awards & Recognition</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    'Best Short Doc — Silver Screen (Prague)',
                    'Best Story — Toronto/LA Doc Festival',
                    'Excellence Award — Docs Without Borders',
                    'Official Selection — Berlin Lift-Off',
                    'Honorable Mention — Hollywood ShortsFest',
                    '12+ Telly Awards',
                    'Kodak Film Scholarship (1999)',
                    'IMCINE Production Grants'
                  ].map((credit) => (
                    <span key={credit} className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">{credit}</span>
                  ))}
                </div>
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Approach */}
      <section className="py-20 sm:py-28 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Approach</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-14">
              A visual philosophy.
            </h2>
          </SectionReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { title: 'Human Connection', desc: 'Every project begins with people. The subjects, the collaborators, the audience. Cinema is empathy made visible.' },
              { title: 'Cinematic Precision', desc: 'Light, composition, movement — these are not technical decisions but storytelling choices that shape how an audience feels.' },
              { title: 'Place & Identity', desc: 'Mexico is not a backdrop. It is a character. Understanding the relationship between place and story is central to how we work.' },
              { title: 'Integrated Vision', desc: 'From direction to cinematography to color — a unified creative perspective that carries through every stage of production.' }
            ].map((item, i) => (
              <SectionReveal key={item.title} delay={i * 0.1}>
                <div className="p-8 rounded-lg border border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg))]">
                  <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-3">{item.title}</h3>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{item.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Director Work */}
      <section className="py-20 sm:py-28">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Selected Projects</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-10">
              Directed Work
            </h2>
          </SectionReveal>

          {/* Project Grid - Using WorkPage pattern */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {[
              {
                slug: 'san-esteban',
                title: 'San Esteban: Freedom, Mysticism and Nopales',
                category: 'Documentary',
                year: '2025',
                thumbnail: 'https://filmfreeway-production-storage-01-connector.filmfreeway.com/press_kits/posters/003/639/002/original/c93512a45a-poster.jpg?1772234101'
              },
              {
                slug: 'el-batan',
                title: 'El Batán, Small and Pretty',
                category: 'Documentary',
                year: '2024',
                thumbnail: 'https://filmfreeway-production-storage-01-connector.filmfreeway.com/press_kits/posters/002/873/095/original/bb12286a4b-poster.jpg?1729710817'
              },
              {
                slug: 'patron-mexican-heritage',
                title: 'Patrón — Mexican Heritage',
                category: 'Commercial',
                year: '2023',
                thumbnail: '/assets/carousel/Mexican Heritage Tin.jpg'
              },
              {
                slug: 'patron-smoky',
                title: 'Patrón — Smoky',
                category: 'Commercial',
                year: '2023',
                thumbnail: '/assets/making-of/patron-smoky.jpg'
              }
            ].map((project, i) => (
              <SectionReveal key={project.slug} delay={i * 0.05}>
                <Link 
                  to={`/work/${project.slug}`} 
                  className="group block"
                  data-testid={`director-project-${project.slug}`}
                >
                  <div className="relative aspect-[2/3] rounded-md overflow-hidden bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] mb-2">
                    <img 
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] uppercase mb-1">{project.category} · {project.year}</p>
                  <h3 className="font-display text-sm text-[hsl(var(--bk-fg))] tracking-tight line-clamp-2">{project.title}</h3>
                </Link>
              </SectionReveal>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/work"
              className="inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity"
            >
              View All Work <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 sm:py-36 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 text-center">
          <SectionReveal>
            <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] text-[hsl(var(--bk-fg))]">
              Interested in collaborating?
            </h2>
            <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mt-4 max-w-[500px] mx-auto">
              Paco works selectively across documentary, narrative, and brand storytelling.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 font-body text-sm px-8 py-3.5 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity active:scale-[0.98] mt-10"
            >
              Get in Touch <ArrowRight size={16} />
            </Link>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
